import React, {
  useEffect,
  useState,
} from "react";

import {
  InvoiceResponse,
} from "@/api";

import InvoiceDetailsView from "./InvoiceDetailsView";
import InvoiceDetailsEdit from "./InvoiceDetailsEdit";

import {
  AdminOrderUpdateService,
} from "../../service/adminOrderUpdateServices";

export interface InvoiceDetailsSectionProps {
  orderId: number;

  value: InvoiceResponse;

  onChange: (
    value: InvoiceResponse
  ) => void;
}

export default function InvoiceDetailsSection({
  orderId,
  value,
  onChange,
}: InvoiceDetailsSectionProps) {

  const [editing, setEditing] =
    useState(false);

  const [draft, setDraft] =
    useState<InvoiceResponse>(value);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  /*
   * --------------------------------------------------
   * Synchronize draft when not editing
   * --------------------------------------------------
   */

  useEffect(() => {
    if (!editing) {
      setDraft(value);
    }
  }, [value, editing]);

  /*
   * --------------------------------------------------
   * Edit
   * --------------------------------------------------
   */

  const handleEdit = () => {
    setDraft(value);
    setError(null);
    setEditing(true);
  };

  /*
   * --------------------------------------------------
   * Save
   * --------------------------------------------------
   */

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!draft.id) {
        throw new Error(
          "Invoice ID is missing.",
        );
      }

      /*
       * Send only editable invoice fields.
       *
       * Adjust this payload according to the actual
       * fields exposed by InvoiceUpdate.
       */
      await AdminOrderUpdateService.updateInvoice(
        draft.id,
        {

          supply_place:
            draft.supply_place,

          supply_state:
            draft.supply_state,

          supply_state_code:
            draft.supply_state_code,

          requestor_type:
            draft.requestor_type,

          requestor_vendor_code:
            draft.requestor_vendor_code,

          requestor_vendor_spoc:
            draft.requestor_vendor_spoc,

          gst_number:
            draft.gst_number,

          pan_number:
            draft.pan_number,

          hsn_sac:
            draft.hsn_sac,

          sales_by:
            draft.sales_by,
        },
      );

      /*
       * Parent refreshes the complete AdminOrderResponse.
       *
       * This makes the backend the authoritative source
       * after the update.
       */
      await onChange(draft);

      /*
       * Only leave edit mode after both the update
       * and refresh have succeeded.
       */
      setEditing(false);

    } catch (err) {
      console.error(
        "Failed to update invoice",
        err,
      );

      setError(
        err instanceof Error
          ? err.message
          : "Failed to update invoice.",
      );
    } finally {
      setSaving(false);
    }
  };

  /*
   * --------------------------------------------------
   * Cancel
   * --------------------------------------------------
   */

  const handleCancel = () => {
    setDraft(value);
    setError(null);
    setEditing(false);
  };

  return (
    <section className="rounded-xl border bg-white p-6">

      {/* =================================================
          EDIT MODE
      ================================================= */}

      {editing ? (
        <>
          <SectionHeader
            title="Invoice Details"
            description="Edit invoice and billing information."
            editing
            saving={saving}
            onCancel={handleCancel}
            onSave={() => void handleSave()}
          />

          <InvoiceDetailsEdit
            value={draft}
            onChange={setDraft}
          />

          {error && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </>
      ) : (

        /* =================================================
           VIEW MODE
        ================================================= */

        <>
          <SectionHeader
            title="Invoice Details"
            description="Review invoice and billing information."
            onEdit={handleEdit}
          />

          <InvoiceDetailsView
            value={value}
          />
        </>
      )}
    </section>
  );
}

/*
 * ==================================================
 * HEADER
 * ==================================================
 */

interface SectionHeaderProps {
  title: string;
  description: string;

  editing?: boolean;
  saving?: boolean;

  onEdit?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
}

function SectionHeader({
  title,
  description,
  editing = false,
  saving = false,
  onEdit,
  onCancel,
  onSave,
}: SectionHeaderProps) {

  return (
    <div className="mb-6 flex items-start justify-between gap-4">

      <div>
        <h2 className="text-lg font-semibold">
          {title}
        </h2>

        <p className="mt-1 text-sm text-gray-500">
          {description}
        </p>
      </div>

      {editing ? (
        <div className="flex shrink-0 gap-2">

          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-md border px-3 py-2 text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="rounded-md bg-black px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving
              ? "Saving..."
              : "Save"}
          </button>

        </div>
      ) : (
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md border px-3 py-2 text-sm font-medium"
        >
          Edit
        </button>
      )}
    </div>
  );
}